import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

const API_BASE_URL = API_CONFIG.API_BASE_URL;

export interface SubscriptionData {
  planName: string;
  billingCycle: string;
  cost: string;
  nextRenewal: string;
  lastPayment: string;
  usage: {
    users: { current: number; limit: number };
    storage: { current: string; limit: string };
    apiCalls: { current: number; limit: number };
  };
}

export interface SubscriptionResponse {
  success: boolean;
  data: SubscriptionData;
  message: string;
  code: number;
}

/** Plan info nested inside each invoice from fetchCompanySubscriptionDetails */
export interface InvoiceSubscriptionPlan {
  name: string;
  planCode: string;
}

/** Single invoice from Zoho subscription details API */
export interface InvoiceDetail {
  invoice_id: string;
  number: string;
  status: string;
  invoice_date: string;
  due_date: string;
  total: number;
  balance: number;
  payment_made: string | null;
  currency_code: string;
  currency_symbol: string;
  /** Plan name and code for this invoice (use for Plan Name column) */
  subscriptionPlan?: InvoiceSubscriptionPlan;
}

/** Usage statistics from fetchCompanySubscriptionDetails (limit = total, available = remaining) */
export interface UsageStatisticsFromApi {
  channelLimit: number;
  messageLimit: number;
  StorageLimit: number;
  channelavailable: number;
  messageavailable: number;
  Storageavailable: number;
}

/**
 * Normalize usage stats from the API for UI: NaN and negative values become 0.
 * Some responses use -1 as a sentinel; avoids labels like "0 of -1" in upgrade modals.
 */
export function nonNegativeUsageStat(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Full response from fetchCompanySubscriptionDetails */
export interface CompanySubscriptionDetailsData {
  currentPlan: string;
  currentPlanCode: string;
  planPeriod: string;
  subscriptionId: string;
  customerId: string;
  userInfo: { admin: number; users: number };
  currentCost: string;
  nextInvoiceDate: string;
  companyGroupCount: number;
  sharedInboxCount: number;
  companyChannelCount: number;
  aiResponseCount: number;
  storageCount: number;
  invoiceDetails: InvoiceDetail[];
  totalCost: string;
  status: string;
  currentPlanStatus: string;
  addonDetails: unknown;
  metadata: unknown;
  expiresAt: string;
  /** Usage stats: limit = total capacity, available = remaining (used = limit - available) */
  usageStatistics?: UsageStatisticsFromApi;
}

export interface CompanySubscriptionDetailsResponse {
  success: boolean;
  data: CompanySubscriptionDetailsData;
  message: string;
  code: number;
}

// Get current subscription data
export const getCurrentSubscription = async (): Promise<SubscriptionResponse> => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    if (!token) {
      throw new Error('No refresh token found');
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.COMPANY.MY_SUBSCRIPTION}`;
    console.log('Fetching subscription from URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Subscription API error:', {
        status: response.status,
        statusText: response.statusText,
        url,
        errorData
      });
      throw new Error(errorData.message || `Failed to fetch subscription: ${response.status}`);
    }

    // Parse the response and map the Plan data to our SubscriptionData format
    const responseData = await response.json();
    const planData = responseData.data;
    const subscriptionData: SubscriptionData = {
      planName: planData.name || 'Professional Plan',
      billingCycle: 'Monthly', // Default since Plan doesn't have this field
      cost: `$${planData.monthlyPrice || '99.00'}`,
      nextRenewal: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to 30 days from now
      lastPayment: new Date().toISOString().split('T')[0], // Default to today
      usage: {
        users: { current: 5, limit: parseInt(planData.subscriberCount) || 10 },
        storage: { current: '2.5 GB', limit: '10 GB' },
        apiCalls: { current: 15000, limit: 50000 },
      },
    };

    return {
      success: true,
      data: subscriptionData,
      message: responseData.message || 'Subscription data fetched successfully',
      code: responseData.code || 200,
    };
  } catch (error) {
    console.error('Get subscription error:', error);
    throw error;
  }
};

/**
 * Fetch company subscription details including invoice list (Zoho payment gateway).
 * POST with companyId; returns current plan info and invoiceDetails for the invoice table.
 */
export const fetchCompanySubscriptionDetails = async (
  companyId: string
): Promise<CompanySubscriptionDetailsResponse> => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    if (!token) {
      throw new Error('No refresh token found');
    }
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.FETCH_COMPANY_SUBSCRIPTION_DETAILS}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch subscription details: ${response.status}`
      );
    }

    const result: CompanySubscriptionDetailsResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Fetch company subscription details error:', error);
    throw error;
  }
};

/**
 * Builds the invoice download URL for Zoho (GET invoice file).
 * Used for both download and view-in-modal; call with auth when fetching.
 */
export function getInvoiceDownloadUrl(
  invoiceId: string,
  platformName: string
): string {
  const params = new URLSearchParams({ platform_name: platformName });
  return `${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.INVOICE_DOWNLOAD(invoiceId)}?${params}`;
}

/**
 * Fetches invoice file as blob (e.g. PDF) for viewing or downloading.
 * GET /api/v1/payment-gateway/zoho/invoices/{invoice_id}/download?platform_name={platform_name}
 */
export async function fetchInvoiceBlob(
  invoiceId: string,
  platformName: string
): Promise<Blob> {
  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
  if (!token) {
    throw new Error('No refresh token found');
  }
  const url = getInvoiceDownloadUrl(invoiceId, platformName);
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf,*/*',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch invoice: ${response.status}`
    );
  }
  return response.blob();
}

/**
 * Downloads the invoice file by calling the Zoho invoice download API
 * and triggering a browser download.
 */
export async function downloadInvoice(
  invoiceId: string,
  platformName: string,
  fileName?: string
): Promise<void> {
  const blob = await fetchInvoiceBlob(invoiceId, platformName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `invoice-${invoiceId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Response shape from subscription validation API.
 * data contains canCreateChannel, canCreateMessage, canCreateAttachment (boolean | null).
 */
export interface SubscriptionValidationResponse {
  success: boolean;
  data: {
    canCreateChannel?: boolean | null;
    canCreateMessage?: boolean | null;
    canCreateAttachment?: boolean | null;
    [key: string]: unknown;
  };
  message: string;
  code: number;
}

/**
 * Result of validateSubscription: permission flags from API and optional message.
 * Only true explicitly allows; false or null means restricted.
 */
export interface SubscriptionValidationResult {
  /** True only when API success and code 200 (request succeeded) */
  success: boolean;
  /** True only when data.canCreateChannel === true; false or null means channel creation restricted */
  canCreateChannel: boolean;
  /** True only when data.canCreateMessage === true; false or null means message posting restricted */
  canCreateMessage: boolean;
  /** True only when data.canCreateAttachment === true (for future use) */
  canCreateAttachment: boolean;
  /** API or error message to show when restricted */
  message?: string;
}

/**
 * Validates subscription for channel creation or message posting.
 * POST /api/v1/payment-gateway/zoho/subscriptions/validation/{user_id}
 * Optional body: { channelId }. Response data includes canCreateChannel, canCreateMessage, canCreateAttachment.
 * Only explicit true allows; false or null means restricted.
 */
export const validateSubscription = async (
  userId: string,
  channelId?: string
): Promise<SubscriptionValidationResult> => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    if (!token) {
      return {
        success: false,
        canCreateChannel: false,
        canCreateMessage: false,
        canCreateAttachment: false,
        message: 'Authentication required',
      };
    }
    if (!userId) {
      return {
        success: false,
        canCreateChannel: false,
        canCreateMessage: false,
        canCreateAttachment: false,
        message: 'User ID is required',
      };
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.SUBSCRIPTION_VALIDATION(userId)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(channelId ? { channelId } : {}),
    });

    const data: SubscriptionValidationResponse = await response.json().catch(() => ({
      success: false,
      data: {},
      message: 'Invalid response from subscription validation',
      code: response.status,
    }));

    const ok = data.success === true && data.code === 200;
    const canCreateChannel = data.data?.canCreateChannel === true;
    const canCreateMessage = data.data?.canCreateMessage === true;
    const canCreateAttachment = data.data?.canCreateAttachment === true;

    return {
      success: ok,
      canCreateChannel,
      canCreateMessage,
      canCreateAttachment,
      message: data.message || (ok ? undefined : 'Subscription validation failed'),
    };
  } catch (error) {
    console.error('Subscription validation error:', error);
    return {
      success: false,
      canCreateChannel: false,
      canCreateMessage: false,
      canCreateAttachment: false,
      message: error instanceof Error ? error.message : 'Subscription validation failed',
    };
  }
};

// Get usage statistics - using mock data since no API exists
export const getUsageStatistics = async (): Promise<SubscriptionResponse> => {
  // Return mock usage data since there's no usage API
  return {
    success: true,
    data: {
      planName: 'Professional Plan',
      billingCycle: 'Monthly',
      cost: '$99.00',
      nextRenewal: '2024-02-15',
      lastPayment: '2024-01-15',
      usage: {
        users: { current: 5, limit: 10 },
        storage: { current: '2.5 GB', limit: '10 GB' },
        apiCalls: { current: 15000, limit: 50000 },
      },
    },
    message: 'Mock usage data',
    code: 200,
  };
};
